// ════════════════════════════════════════════════════════════════════════════
// TableCheckoutPage — /t/:locationId/:tableId/checkout — kiosk-styled checkout
// for order-at-table: review items, customer info, promo code, tip, and the
// payment choice (Pay now — card/Apple Pay/Google Pay — vs Pay at the counter).
// Card payments ask open-vs-closed (keep the tab open for more orders vs pay &
// close). Success lands on the orange confirmation screen (kiosk parity).
// ════════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../firebaseConfig';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
const money = (v) => `$${(Number(v) || 0).toFixed(2)}`;

const TableCheckoutPageInner = () => {
  const { locationId, tableId } = useParams();
  const navigate = useNavigate();
  const db = getFirestore();
  const functions = getFunctions();
  const stripe = useStripe();
  const elements = useElements();

  const cartKey = `qrCart_${locationId}_${tableId}`;
  const tabKey = `qrTab_${locationId}_${tableId}`;

  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cartKey) || '[]'); } catch (_) { return []; }
  });
  const [tabRecovered, setTabRecovered] = useState(() => {
    try { const t = JSON.parse(localStorage.getItem(tabKey) || 'null'); return t?.recoveryToken ? t : null; } catch (_) { return null; }
  });

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staffClosed, setStaffClosed] = useState(false);

  // customer info
  const [customerName, setCustomerName] = useState('');
  const [customerContact, setCustomerContact] = useState('');
  // promo
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null); // {code, discountAmount, message}
  const [promoBusy, setPromoBusy] = useState(false);
  // tip
  const [tipSel, setTipSel] = useState(''); // '10'|'15'|'20'|'25'|'custom'|'0'
  const [tipCustom, setTipCustom] = useState('');
  const [tipIsPercent, setTipIsPercent] = useState(true);
  // payment
  const [payMethod, setPayMethod] = useState('card'); // 'card' | 'counter' — card first on purpose ("pay now" is the suggestion)
  const [keepOpen, setKeepOpen] = useState(false); // card: keep the tab open vs pay & close
  const [placing, setPlacing] = useState(false);
  const [cardError, setCardError] = useState('');
  const [placed, setPlaced] = useState(null); // success payload for the orange screen

  // ── context + live staff-close detection ───────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const fn = httpsCallable(functions, 'getTableContext');
        const rt = tabRecovered?.recoveryToken ? null : new URLSearchParams(window.location.search).get('rt');
        const { data } = await fn({ locationId, tableId, ...(rt ? { rt } : {}) });
        if (!data.success) throw new Error('This QR is no longer valid.');
        setCtx(data);
        if (data.recovered && !tabRecovered) {
          const rec = { recoveryToken: data.recovered.recoveryToken, guestNumber: data.recovered.guestNumber, tabName: `Order #${data.recovered.guestNumber}`, items: data.recovered.items || [], total: data.recovered.total || 0 };
          setTabRecovered(rec);
          try { localStorage.setItem(tabKey, JSON.stringify(rec)); } catch (_) {}
          window.history.replaceState({}, '', window.location.pathname);
        }
        setLoading(false);
        if (data.openTab?.tabId) {
          onSnapshot(doc(db, 'users', data.businessId, 'openTabs', data.openTab.tabId), (snap) => {
            const st = snap.data()?.status;
            if (snap.exists && st && st !== 'open') setStaffClosed(true);
          });
        }
      } catch (e) {
        setError(e.message || 'Could not load the checkout.');
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, tableId]);

  const subtotal = cart.reduce((a, i) => a + i.price * i.qty, 0);
  const taxRate = ctx?.taxRate || 0;
  const tax = subtotal * (taxRate / 100);
  const promoDiscount = appliedPromo?.discountAmount || 0;
  const base = Math.max(0, subtotal - promoDiscount);
  const tipDollars = tipSel === 'custom' ? (parseFloat(tipCustom) || 0) : tipSel === '0' ? 0 : (parseFloat(tipSel) || 0) * (tipIsPercent ? base / 100 : 1);
  const total = base * (1 + taxRate / 100) + tipDollars;

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    setCardError('');
    try {
      const fn = httpsCallable(functions, 'validatePromoCode');
      const { data } = await fn({
        businessId: ctx.businessId,
        locationId,
        code: promoInput.trim(),
        salesChannel: 'online orders',
        items: cart.map((i) => ({ productId: i.productId, categoryId: i.categoryId || null, categoryName: '', price: i.price, qty: i.qty, total: i.price * i.qty })),
        cartSubtotal: subtotal,
        commit: false,
      });
      if (data.success) {
        // discountAmount comes in DOLLARS from validatePromoCode
        setAppliedPromo({ code: promoInput.trim().toUpperCase(), discountAmount: Number(data.discountAmount) || 0, message: data.message || '' });
      } else {
        setCardError(data.message || 'Invalid promo code');
      }
    } catch (e) {
      setCardError('Could not validate the promo code.');
    } finally {
      setPromoBusy(false);
    }
  };

  // ── guest closes their own account (#5): pay what's owed first, else close ──
  const [closing, setClosing] = useState(false);
  const handleCloseTab = async () => {
    if (!tabRecovered?.recoveryToken) return;
    if ((tabRecovered?.unpaidAmount || 0) > 0.004) {
      setCardError('There is still an unpaid balance — pay it with "Pay now", then close.');
      return;
    }
    setClosing(true);
    try {
      const fn = httpsCallable(functions, 'closeTableTab');
      const { data } = await fn({ locationId, tableId, recoveryToken: tabRecovered.recoveryToken });
      if (!data.success) throw new Error('Could not close the tab');
      try { localStorage.removeItem(tabKey); } catch (_) {}
      setTabRecovered(null);
      setPlaced({ guestNumber: data.guestNumber, paid: true, closed: true, total: data.total });
    } catch (e) {
      setCardError(e.message || 'Could not close the tab');
    } finally {
      setClosing(false);
    }
  };

  // ── place / pay ─────────────────────────────────────────────────────────────
  const handleSubmit = async (walletEvent = null) => {
    if (!customerName.trim()) { setCardError('Please add a name for the order'); return; }
    setPlacing(true);
    setCardError('');
    try {
      let tabToken = tabRecovered?.recoveryToken || null;
      let guestNumber = tabRecovered?.guestNumber || null;
      let confirmationNumber = null;

      // 1. If there are NEW cart items, place them first (new tab or append)
      if (cart.length > 0) {
        const placeFn = httpsCallable(functions, 'placeTableOrder');
        const { data } = await placeFn({
          locationId, tableId,
          customerName: customerName.trim(),
          customerPhone: customerContact.trim(),
          items: cart.map((i) => ({ productId: i.productId, qty: i.qty, instructions: i.instructions || null, options: i.options || [], optionPicks: i.optionPicks || [] })),
          ...(tabToken ? { recoveryToken: tabToken } : {}),
        });
        if (!data.success) throw new Error('Could not place the order');
        guestNumber = data.guestNumber || guestNumber;
        confirmationNumber = data.confirmationNumber || null;
        tabToken = data.recoveryToken || tabToken; // new tabs return a token to their creator
      }

      // 2. Pay now (card/wallet) → pay the tab's owed amount
      if (payMethod === 'card') {
        let pmId = null;
        if (walletEvent) {
          const { error: submitError } = await elements.submit();
          if (submitError) throw submitError;
          const { error: pmError, paymentMethod: pm } = await stripe.createPaymentMethod({ elements });
          if (pmError || !pm) throw (pmError || new Error('Could not read the wallet card'));
          pmId = pm.id;
        } else {
          const cardEl = elements.getElement(CardElement);
          const { error: pmError, paymentMethod: pm } = await stripe.createPaymentMethod({
            type: 'card', card: cardEl, billing_details: { name: customerName.trim() },
          });
          if (pmError) throw pmError;
          pmId = pm.id;
        }
        if (!tabToken) throw new Error('Could not link the payment to the table — please refresh and recover your order.');

        const payFn = httpsCallable(functions, 'payTableTab');
        let { data } = await payFn({ locationId, tableId, recoveryToken: tabToken, tipDollars, paymentMethodId: pmId, keepOpen });
        if (data.status === 'requires_action') {
          const { paymentIntent: handled, error: actionError } = await stripe.handleCardAction(data.clientSecret);
          if (actionError) throw actionError;
          if (handled.status !== 'succeeded') throw new Error(`Payment status: ${handled.status}`);
          const again = await payFn({ locationId, tableId, recoveryToken: tabToken, tipDollars, paymentIntentId: data.paymentIntentId, keepOpen });
          data = again.data;
        }
        if (!data.success) throw new Error('Payment failed');
      }

      // 3. Success state + clear cart
      setPlaced({ guestNumber, confirmationNumber, paid: payMethod === 'card', total, keepOpen });
      setCart([]);
      try { localStorage.removeItem(cartKey); } catch (_) {}
      if (!keepOpen || payMethod !== 'card') { /* tab session persists for counter payments */ }
    } catch (e) {
      setCardError(e.message || 'Something went wrong. Please try again or ask the staff.');
    } finally {
      setPlacing(false);
    }
  };

  // ── renders ─────────────────────────────────────────────────────────────────
  if (loading) return <div style={S.center}><div className="spinner-border" style={{ color: '#ff5722' }}></div></div>;
  if (error) return <div style={S.center}><p style={{ color: '#dc2626' }}>{error}</p></div>;

  // ── The ORANGE confirmation screen (kiosk-machine parity) ──
  if (placed) {
    return (
      <div style={S.orangePage}>
        <div style={S.checkCircle}><i className="fas fa-check" style={{ color: '#ff6b35', fontSize: 44 }}></i></div>
        <h1 style={{ color: '#fff', fontWeight: 800, marginTop: 18 }}>{placed.closed ? 'Tab closed — thank you!' : placed.paid ? 'Payment received!' : 'Order placed!'}</h1>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16 }}>{ctx.locationName} · {ctx.tableName}</p>
        {placed.guestNumber && (
          <div style={S.guestCard}>
            <div style={{ fontSize: 13, color: '#c2410c', fontWeight: 700 }}>YOUR GUEST NUMBER</div>
            <div style={{ fontSize: 44, fontWeight: 800, color: '#ea580c', letterSpacing: 3 }}>#{placed.guestNumber}</div>
            {placed.confirmationNumber && <div style={{ fontSize: 12, color: '#9a3412' }}>Confirmation {placed.confirmationNumber}</div>}
          </div>
        )}
        <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, maxWidth: 360, textAlign: 'center' }}>
          {placed.paid
            ? (placed.keepOpen ? 'Paid — your tab stays open, keep ordering whenever you like.' : 'All set — your order is closed. Thank you!')
            : 'The kitchen is on it. Pay at the register when you are ready.'}
        </p>
        <button style={S.whiteBtn} onClick={() => navigate(`/t/${locationId}/${tableId}`)}>
          {placed.keepOpen || !placed.paid ? 'Keep ordering' : 'Back to menu'}
        </button>
      </div>
    );
  }

  const hasTab = !!tabRecovered;
  const owedNow = total; // new items
  const tabTotal = tabRecovered?.total || 0;

  return (
    <div style={S.page}>
      {/* header */}
      <div style={S.header}>
        <button style={{ background: 'none', border: 'none', color: '#ff5722', fontSize: 15, fontWeight: 700 }} onClick={() => navigate(`/t/${locationId}/${tableId}`)}>
          <i className="fas fa-arrow-left mr-1"></i>Menu
        </button>
        <div style={{ fontWeight: 800 }}>Checkout</div>
        <div style={{ width: 60 }}></div>
      </div>

      {staffClosed && (
        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 10, padding: 12, fontSize: 13, color: '#991b1b', marginBottom: 10 }}>
          The staff closed this table's order. If anything looks off, please ask them.
        </div>
      )}

      {/* items */}
      <div style={S.card}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Review — {ctx.tableName}{hasTab ? ` · adding to order #${tabRecovered.guestNumber}` : ''}</div>
        {cart.length === 0 && !hasTab && <p style={{ color: '#888' }}>Your cart is empty — go back and pick something good.</p>}
        {cart.length === 0 && hasTab && <p style={{ color: '#888' }}>Nothing new in the cart — you can still pay/close your open order below.</p>}
        {cart.map((i) => (
          <div key={`${i.productId}::${(i.options || []).join('|')}::${i.instructions || ''}`} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 600 }}>{i.qty}× {i.productName}</div>
              {(i.options || []).length > 0 && <div style={{ fontSize: 12, color: '#777' }}>+ {i.options.join(', ')}</div>}
              {!!i.instructions && <div style={{ fontSize: 12, color: '#e65100', fontStyle: 'italic' }}>"{i.instructions}"</div>}
            </div>
            <div style={{ fontWeight: 700 }}>{money(i.price * i.qty)}</div>
          </div>
        ))}
      </div>

      {/* customer info */}
      <div style={S.card}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Your info</div>
        <input className="form-control mb-2" placeholder="Name for the order *" value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
        <input className="form-control" placeholder="Phone or email (recommended — recover your order later)" value={customerContact} onChange={(e) => setCustomerContact(e.target.value)} />
      </div>

      {/* promo */}
      <div style={S.card}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Promo code</div>
        {appliedPromo ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#059669', fontWeight: 700 }}><i className="fas fa-tag mr-1"></i>{appliedPromo.code} · −{money(appliedPromo.discountAmount)}</span>
            <button style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 13 }} onClick={() => setAppliedPromo(null)}>Remove</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="form-control" placeholder="Code" value={promoInput} onChange={(e) => setPromoInput(e.target.value)} />
            <button className="btn btn-outline-secondary" disabled={promoBusy} onClick={applyPromo}>{promoBusy ? '…' : 'Apply'}</button>
          </div>
        )}
      </div>

      {/* tip */}
      <div style={S.card}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Tip</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['0', '10', '15', '20', '25'].map((p) => (
            <button key={p} style={tipSel === p && tipIsPercent ? S.tipChipOn : S.tipChip}
              onClick={() => { setTipSel(p); setTipIsPercent(true); }}>
              {p === '0' ? 'No tip' : `${p}%`}
            </button>
          ))}
          <button style={tipSel === 'custom' ? S.tipChipOn : S.tipChip} onClick={() => { setTipSel('custom'); setTipIsPercent(false); }}>Custom $</button>
        </div>
        {tipSel === 'custom' && (
          <input className="form-control mt-2" placeholder="Tip amount $" inputMode="decimal" value={tipCustom} onChange={(e) => setTipCustom(e.target.value)} />
        )}
      </div>

      {/* totals */}
      <div style={S.card}>
        <div style={S.totalRow}><span>Subtotal</span><span>{money(subtotal)}</span></div>
        {promoDiscount > 0 && <div style={S.totalRow}><span style={{ color: '#059669' }}>Promo ({appliedPromo.code})</span><span style={{ color: '#059669' }}>−{money(promoDiscount)}</span></div>}
        <div style={S.totalRow}><span>Tax ({taxRate}%)</span><span>{money(base * (taxRate / 100))}</span></div>
        {tipDollars > 0 && <div style={S.totalRow}><span>Tip</span><span>{money(tipDollars)}</span></div>}
        {hasTab && <div style={S.totalRow}><span style={{ color: '#777' }}>Your open order so far</span><span style={{ color: '#777' }}>{money(tabTotal)}</span></div>}
        <div style={{ ...S.totalRow, fontWeight: 800, fontSize: 22, borderTop: '2px solid #f0f0f0', paddingTop: 10, marginTop: 6 }}>
          <span>{hasTab ? 'New items total' : 'Total'}</span><span style={{ color: '#ff5722' }}>{money(owedNow)}</span>
        </div>
      </div>

      {/* payment method — card FIRST (the indirect "pay now" suggestion) */}
      <div style={S.card}>
        <div style={{ fontWeight: 800, marginBottom: 10 }}>Payment</div>
        <div style={{ display: 'flex', gap: 10 }}>
          {ctx.cardOnline && (
            <button style={payMethod === 'card' ? S.payBtnOn : S.payBtn} onClick={() => setPayMethod('card')}>
              <i className="fas fa-credit-card" style={{ display: 'block', fontSize: 22, marginBottom: 6 }}></i>
              Pay now
              <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>card · Apple Pay · Google Pay</div>
            </button>
          )}
          {ctx.payAtCounter && (
            <button style={payMethod === 'counter' ? S.payBtnOn : S.payBtn} onClick={() => setPayMethod('counter')}>
              <i className="fas fa-cash-register" style={{ display: 'block', fontSize: 22, marginBottom: 6 }}></i>
              At the counter
              <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>pay when you're done</div>
            </button>
          )}
        </div>

        {payMethod === 'card' && (
          <div style={{ marginTop: 12 }}>
            <ExpressCheckoutElement
              onConfirm={(e) => handleSubmit(e)}
              options={{
                buttonType: { applePay: 'check-out', googlePay: 'pay' },
                paymentMethods: { applePay: 'always', googlePay: 'always', link: 'never', paypal: 'never', amazonPay: 'never' },
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', margin: '10px 0', color: '#aaa', fontSize: 12 }}>
              <div style={{ flex: 1, height: 1, background: '#e5e7eb' }}></div>
              <span style={{ padding: '0 10px' }}>or type a card</span>
              <div style={{ flex: 1, height: 1, background: '#e5e7eb' }}></div>
            </div>
            <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 10, padding: 10 }}>
              <CardElement options={{ style: { base: { fontSize: '15px' } } }} />
            </div>

            {/* open vs closed — the user's #4: ask when paying by card */}
            <div style={{ marginTop: 12, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: 10 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#9a3412', cursor: 'pointer' }}>
                <input type="checkbox" checked={keepOpen} onChange={(e) => setKeepOpen(e.target.checked)} style={{ marginTop: 3 }} />
                <span><strong>Keep my tab open.</strong> Pay for these items now and keep ordering — staff will close my table when I'm done. (Off = pay & close the order.)</span>
              </label>
            </div>
          </div>
        )}

        {cardError && <div className="text-danger mt-2" style={{ fontSize: 13 }}>{cardError}</div>}
      </div>

      {/* close the account (guest-initiated, #5) — only with a recovered tab
          and nothing new in the cart (pay/add those first) */}
      {hasTab && cart.length === 0 && !staffClosed && (
        <div style={{ ...S.card, border: '1px solid #fecaca' }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Done for the night?</div>
          <p style={{ fontSize: 12, color: '#777', marginBottom: 8 }}>
            Your order is {tabRecovered?.unpaidAmount > 0.004 ? `still owed ${money(tabRecovered.unpaidAmount)} — pay it above first` : 'all paid up'}.
          </p>
          <button
            style={{ ...S.bigOrange, background: '#1f2937', marginTop: 0, padding: 12, fontSize: 15 }}
            disabled={closing || (tabRecovered?.unpaidAmount || 0) > 0.004}
            onClick={handleCloseTab}>
            {closing ? 'Closing…' : 'Close my tab'}
          </button>
        </div>
      )}

      {/* submit */}
      <button style={S.bigOrange} disabled={placing || (cart.length === 0 && !hasTab)} onClick={() => handleSubmit(null)}>
        {placing ? 'Processing…' : payMethod === 'card' ? `Pay ${money(owedNow + (hasTab ? tabTotal : 0))} now` : 'Send to kitchen — pay at the counter'}
      </button>
      <p style={{ textAlign: 'center', fontSize: 12, color: '#999', marginTop: 8 }}>
        {payMethod === 'counter' ? 'The kitchen starts right away.' : 'Secure payment by Stripe.'}
      </p>
    </div>
  );
};

const S = {
  page: { minHeight: '100vh', background: '#f8f9fa', padding: 16, maxWidth: 560, margin: '0 auto', fontFamily: "'Poppins', sans-serif" },
  center: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Poppins', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  card: { background: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  totalRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 6, color: '#555' },
  tipChip: { border: '1px solid #ddd', background: '#fff', borderRadius: 18, padding: '6px 14px', fontSize: 13, fontWeight: 600 },
  tipChipOn: { border: '1px solid #ff5722', background: '#ff5722', color: '#fff', borderRadius: 18, padding: '6px 14px', fontSize: 13, fontWeight: 600 },
  payBtn: { flex: 1, border: '2px solid #e5e7eb', background: '#fff', borderRadius: 14, padding: '14px 8px', fontWeight: 700, color: '#555' },
  payBtnOn: { flex: 1, border: '2px solid #ff5722', background: '#fff3ee', borderRadius: 14, padding: '14px 8px', fontWeight: 700, color: '#ff5722' },
  bigOrange: { width: '100%', background: '#ff5722', color: '#fff', border: 'none', borderRadius: 14, padding: 16, fontSize: 18, fontWeight: 800, marginTop: 4 },
  orangePage: { minHeight: '100vh', background: 'linear-gradient(160deg, #ff6b35 0%, #ff5722 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'Poppins', sans-serif" },
  checkCircle: { width: 96, height: 96, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  guestCard: { background: '#fff', borderRadius: 16, padding: '14px 28px', marginTop: 16, textAlign: 'center' },
  whiteBtn: { background: '#fff', color: '#ff5722', border: 'none', borderRadius: 14, padding: '14px 34px', fontSize: 16, fontWeight: 800, marginTop: 18 },
};

const TableCheckoutPage = () => {
  const { locationId, tableId } = useParams();
  // ExpressCheckoutElement needs Elements initialized with mode+amount
  // (deferred PI creation) — otherwise Stripe logs "Unable to select session".
  let approxCents = 100;
  try {
    const cart = JSON.parse(localStorage.getItem(`qrCart_${locationId}_${tableId}`) || '[]');
    const sub = cart.reduce((a, i) => a + (i.price || 0) * (i.qty || 1), 0);
    approxCents = Math.max(100, Math.round(sub * 100));
  } catch (_) {}
  return (
    <Elements stripe={stripePromise} options={{ mode: 'payment', amount: approxCents, currency: 'usd' }}>
      <TableCheckoutPageInner />
    </Elements>
  );
};

export default TableCheckoutPage;
